import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { API, useAuth, getToken } from '../App';
import AuthModal from './AuthModal';
import { calculateAll, getStarSign, fmtNum } from '../utils/numerology';

const STAGES = [
  { id: 'newborn', label: 'Newborn', age: '0 — 12 months' },
  { id: 'baby', label: 'Baby', age: '1 — 2 years' },
  { id: 'toddler', label: 'Toddler', age: '2 — 3 years' },
  { id: 'preschool', label: 'Preschool', age: '3 — 5 years' },
  { id: 'early-school', label: 'Early School', age: '6 — 8 years' },
  { id: 'late-childhood', label: 'Late Childhood', age: '9 — 12 years' },
  { id: 'teen', label: 'Teen', age: '13 — 16 years' },
  { id: 'young-adult', label: 'Young Adult', age: '17 — 21 years' },
  { id: 'adult', label: 'Adult', age: '22 — 39 years' },
  { id: 'adult-40-plus', label: 'Adult', age: '40+ years' },
];

const LOADING_MESSAGES = [
  'Reading their blueprint...',
  'Weaving the numbers...',
  'Finding the patterns...',
  'Writing your reading...',
];

const CHILD_QA = [
  'What does this mean for school?',
  'How can I best support them?',
  'What are their greatest strengths?',
  'What should I know about their friendships?',
];

const SELF_QA = [
  'How do my core numbers interact?',
  'What are my greatest strengths?',
  'What should I focus on right now?',
];

const YEARS = [];
for (let y = 2026; y >= 1926; y--) YEARS.push(y);

const MONTHS = [
  { v: 1, l: 'January' }, { v: 2, l: 'February' }, { v: 3, l: 'March' },
  { v: 4, l: 'April' }, { v: 5, l: 'May' }, { v: 6, l: 'June' },
  { v: 7, l: 'July' }, { v: 8, l: 'August' }, { v: 9, l: 'September' },
  { v: 10, l: 'October' }, { v: 11, l: 'November' }, { v: 12, l: 'December' },
];

// Headings Claude uses (free reading: 4 sections; legacy heading keys retained for old saved readings)
const KNOWN_HEADINGS = [
  // New format (v3 — Mar 2026): seven titled sections + Breakdown + waitlist tease
  // Self-mode titles
  'Your blueprint at a glance',
  'Who you are at your core',
  'Where you hold tension',
  'How you show up in the world',
  'What your soul is really looking for',
  'Where you are right now',
  "Who you're becoming",
  // Child / other-mode titles
  'Their blueprint at a glance',
  'Who they are at their core',
  'Where they hold tension',
  'How they show up in the world',
  'What their soul is really looking for',
  'Where they are right now',
  "Who they're becoming",
  // Breakdown heading
  "Here's what we used to build your reading",
  // Legacy v1 free-reading headings (still recognised for older saved readings)
  'Their Blueprint', 'Their Shadow', 'Their Strength', 'How to Love Them Well',
  'Your Blueprint', 'Your Shadow', 'Your Strength', 'How to Love Yourself Well',
  "This Year's Invitation",
  'How They Show Up', 'What Their Heart Needs', 'Their Hidden Gift', 'Their Cosmic Story',
  'How You Show Up', 'What Your Heart Needs', 'Your Hidden Gift', 'Your Cosmic Story',
];

// Detect a Breakdown sub-heading line like "Birthday — 14/5", "✦ Soul Urge — 11", "Star Sign — Cancer"
const BREAKDOWN_LABELS = [
  'Birthday', 'Life Path', 'Expression', 'Soul Urge',
  'Personality', 'Maturity', 'Personal Year', 'Star Sign',
];
function isBreakdownSubheading(line) {
  const t = line.trim().replace(/^✦\s*/, '');
  // Must start with a known label, followed by an em-dash or hyphen
  return BREAKDOWN_LABELS.some(label =>
    t.startsWith(label + ' —') || t.startsWith(label + ' -') || t === label
  ) && t.length < 60;
}

// Hook: progressively reveal text word-by-word to feel like it's being written
function useTypewriter(fullText, enabled) {
  const [displayed, setDisplayed] = React.useState('');
  const [isTyping, setIsTyping] = React.useState(false);

  useEffect(() => {
    if (!fullText) { setDisplayed(''); return; }
    if (!enabled) { setDisplayed(fullText); setIsTyping(false); return; }

    setDisplayed('');
    setIsTyping(true);
    // Split by whitespace but preserve newlines
    const tokens = fullText.match(/\S+|\s+/g) || [];
    let i = 0;
    let cancelled = false;

    const pump = () => {
      if (cancelled) return;
      if (i >= tokens.length) {
        setIsTyping(false);
        return;
      }
      const next = tokens[i++];
      setDisplayed(prev => prev + next);
      // Gentle, contemplative cadence — like someone is really writing this
      const prevEnd = next.trim().slice(-1);
      let delay = 26 + Math.random() * 14;
      if (/[.!?]/.test(prevEnd)) delay = 220;
      else if (/[,;:]/.test(prevEnd)) delay = 110;
      else if (/^\s+$/.test(next)) delay = 8;
      setTimeout(pump, delay);
    };

    // small initial pause
    const startT = setTimeout(pump, 320);
    return () => { cancelled = true; clearTimeout(startT); };
  }, [fullText, enabled]);

  return { displayed, isTyping };
}

// Parse reading text into headings + paragraphs
// Render text that may contain **markdown-style bold** for skim-reading
function renderRichText(text) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function parseReading(text) {
  if (!text) return [];
  const lines = text.split('\n');
  const segments = [];
  let paraLines = [];

  const flush = () => {
    const t = paraLines.join(' ').trim();
    if (t) segments.push({ type: 'paragraph', text: t });
    paraLines = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();
    // Skip decorative horizontal rules (---, ***, ___) Claude sometimes inserts
    if (/^[-*_]{3,}$/.test(trimmed)) {
      flush();
      continue;
    }
    const isHeading = trimmed.startsWith('## ')
      || KNOWN_HEADINGS.includes(trimmed)
      || isBreakdownSubheading(trimmed)
      || (trimmed.length > 0 && trimmed.length < 50 && !trimmed.endsWith('.') && !trimmed.endsWith(',') && trimmed === trimmed.replace(/[*_]/g, ''));

    if (isHeading && trimmed.length > 0 && trimmed.length < 55 && paraLines.length === 0 && segments.length > 0) {
      flush();
      segments.push({ type: 'heading', text: trimmed.replace(/^## /, '') });
    } else if (isHeading && (KNOWN_HEADINGS.includes(trimmed) || trimmed.startsWith('## ') || isBreakdownSubheading(trimmed))) {
      flush();
      segments.push({ type: 'heading', text: trimmed.replace(/^## /, '') });
    } else if (trimmed === '') {
      flush();
    } else {
      paraLines.push(trimmed);
    }
  }
  flush();
  return segments.filter(s => s.text);
}

function renderText(text) {
  if (!text) return null;
  return text.split('\n\n').filter(p => p.trim()).map((p, i) => <p key={i}>{p.trim()}</p>);
}

// Reading limit helpers — anonymous visitors get ONE free reading, then the
// in-flow gate prompts them to add their email + name (account-lite) to keep
// going. Once authenticated, the limit no longer applies.
const LIMIT = 1;
function getReadingCount() { return parseInt(localStorage.getItem('lbn_readings_used') || '0', 10); }
function incrementReadingCount() { localStorage.setItem('lbn_readings_used', getReadingCount() + 1); }
function resetReadingCount() { localStorage.setItem('lbn_readings_used', '0'); }

// Printable reading component (hidden on screen, shown on print)
function PrintableReading({ reading }) {
  if (!reading) return null;
  const nums = reading.numbers || {};
  const parsed = parseReading(reading.reading);
  return (
    <div className="print-only" id="printable-reading">
      <div className="print-header">
        <div className="print-logo">Little Blue Numbers</div>
        <div className="print-tagline">Blueprint Readings</div>
      </div>
      <div className="print-name">{reading.name}</div>
      <div className="print-stage">{reading.stage_label}</div>
      <div className="print-numbers">
        {nums.star_sign && <span>{nums.star_sign}</span>}
        <span>Life Path {fmtNum(nums.life_path)}</span>
        <span>Expression {fmtNum(nums.expression)}</span>
        <span>Soul Urge {fmtNum(nums.soul_urge)}</span>
        <span>Personality {fmtNum(nums.personality)}</span>
        <span>Birthday {fmtNum(nums.birthday)}</span>
        <span>Maturity {fmtNum(nums.maturity)}</span>
      </div>
      <div className="print-reading">
        {parsed.map((seg, i) =>
          seg.type === 'heading'
            ? <h3 key={i} className="print-heading">{seg.text}</h3>
            : <p key={i}>{renderRichText(seg.text)}</p>
        )}
      </div>
      <div className="print-footer">Generated by Little Blue Numbers · littlebluenumbers.com</div>
    </div>
  );
}

// Reading limit modal — after the first free reading, ask for email + name
// (account-lite sign-up). They get a token back and can keep reading.
function ReadingAccuracyPrompt({ sessionId, onDone }) {
  const [chosen, setChosen] = useState(null); // 'accurate' | 'mostly' | 'not_really'
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [thanks, setThanks] = useState(false);
  const [showNote, setShowNote] = useState(false);

  const submit = async (accuracy, withNote = false) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/readings/feedback`, {
        session_id: sessionId,
        accuracy,
        note: withNote ? note.trim() || null : null,
      });
      // Remember they've answered for this reading so we don't ask again
      try {
        const seen = JSON.parse(localStorage.getItem('lbn_accuracy_voted') || '[]');
        if (!seen.includes(sessionId)) {
          seen.push(sessionId);
          localStorage.setItem('lbn_accuracy_voted', JSON.stringify(seen.slice(-50)));
        }
      } catch { /* ignore */ }
      setThanks(true);
      setTimeout(() => onDone?.(), 2200);
    } catch {
      // Silent — non-critical, never block the reader
      onDone?.();
    } finally {
      setSubmitting(false);
    }
  };

  if (thanks) {
    return (
      <div className="accuracy-prompt accuracy-prompt-thanks" data-testid="accuracy-prompt-thanks">
        <span className="accuracy-thanks-icon">✦</span>
        <span>Thank you. This shapes everything we build next.</span>
      </div>
    );
  }

  const chip = (value, label) => (
    <button
      key={value}
      type="button"
      className={`accuracy-chip ${chosen === value ? 'active' : ''}`}
      onClick={() => { setChosen(value); setShowNote(true); }}
      disabled={submitting}
      data-testid={`accuracy-chip-${value}`}
    >
      {label}
    </button>
  );

  return (
    <div className="accuracy-prompt" data-testid="accuracy-prompt">
      <div className="accuracy-prompt-head">
        <div className="accuracy-prompt-title">Did this reading feel like them?</div>
        <button
          type="button"
          className="accuracy-skip"
          onClick={() => onDone?.()}
          data-testid="accuracy-skip-btn"
          aria-label="Skip"
        >
          Skip
        </button>
      </div>
      <div className="accuracy-chips">
        {chip('accurate',   'Yes, that\u2019s them')}
        {chip('mostly',     'Mostly')}
        {chip('not_really', 'Not really')}
      </div>
      {showNote && chosen && (
        <div className="accuracy-note-wrap">
          <input
            className="accuracy-note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="A line about what landed (optional)"
            maxLength={500}
            data-testid="accuracy-note-input"
            disabled={submitting}
          />
          <button
            type="button"
            className="accuracy-submit-btn"
            onClick={() => submit(chosen, true)}
            disabled={submitting}
            data-testid="accuracy-submit-btn"
          >
            {submitting ? 'Saving\u2026' : 'Send'}
          </button>
        </div>
      )}
    </div>
  );
}

function hasAccuracyVoted(sessionId) {
  try {
    const seen = JSON.parse(localStorage.getItem('lbn_accuracy_voted') || '[]');
    return seen.includes(sessionId);
  } catch { return false; }
}

// Reading limit modal — after the first free reading, ask for email + name
// (account-lite sign-up). They get a token back and can keep reading.
function ReadingLimitModal({ onSuccess, onSignIn, onDismiss, defaultName }) {
  const { quickRegister } = useAuth();
  const [email, setEmail] = useState('');
  const [name, setName] = useState(defaultName || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const newUser = await quickRegister(name.trim(), email.trim());
      onSuccess?.(newUser);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || "We couldn't save that. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 250 }} data-testid="reading-limit-modal">
      <div className="modal-box" style={{ textAlign: 'center' }}>
        <div className="modal-handle" />
        <div style={{ fontSize: 32, marginBottom: 8 }}>✦</div>
        <div className="modal-title" style={{ marginBottom: 8 }}>Loved your first reading?</div>
        <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 22 }}>
          Pop your name and email below to <strong>create an account and join our waitlist</strong> — you'll be first to know when full readings, family profiles and saved blueprints launch. We'll email you a link to set your password so you can come back any time.
        </p>
        <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
          <label className="field-label">Your name</label>
          <input
            className="field-input"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="First name"
            required
            data-testid="limit-name-input"
            style={{ marginBottom: 10 }}
          />
          <label className="field-label">Email address</label>
          <input
            className="field-input"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            data-testid="limit-email-input"
          />
          {error && (
            <div style={{ background: 'rgba(220,38,38,0.08)', borderRadius: 10, padding: '8px 12px', fontSize: 12, color: '#dc2626', marginTop: 12 }}>
              {error}
            </div>
          )}
          <button
            type="submit"
            className="modal-close"
            disabled={submitting}
            style={{ marginTop: 20, marginBottom: 10 }}
            data-testid="limit-signup-btn"
          >
            {submitting ? 'Saving…' : 'Create account & join waitlist'}
          </button>
        </form>
        <button
          type="button"
          onClick={onSignIn}
          style={{ width: '100%', background: 'none', border: '1.5px solid var(--border-strong)', borderRadius: 100, padding: '12px 24px', fontSize: 13, color: 'var(--body)', cursor: 'pointer', marginBottom: 10, fontFamily: 'inherit' }}
          data-testid="limit-signin-btn"
        >
          I already have an account
        </button>
        <button
          type="button"
          onClick={onDismiss}
          style={{ background: 'none', border: 'none', fontSize: 12, color: 'var(--muted)', cursor: 'pointer', textDecoration: 'underline', padding: '4px 0' }}
          data-testid="limit-dismiss-btn"
        >
          Not right now
        </button>
      </div>
    </div>
  );
}

// Question-limit modal — shown after 3 follow-up questions
function QuestionLimitModal({ onSignUp, onWaitlist, onFeedback, onDismiss }) {
  return (
    <div className="modal-overlay" style={{ zIndex: 252 }} data-testid="question-limit-modal">
      <div className="modal-box" style={{ textAlign: 'center' }}>
        <div className="modal-handle" />
        <div style={{ fontSize: 32, marginBottom: 8 }}>✦</div>
        <div className="modal-title" style={{ marginBottom: 8 }}>Loving this conversation?</div>
        <p style={{ fontSize: 15, color: 'var(--body)', lineHeight: 1.7, marginBottom: 22 }}>
          You've asked three questions already — you're clearly getting real value here. Create an account to keep asking, save this reading, and help shape what we build next.
        </p>
        <button className="modal-close" onClick={onSignUp} style={{ marginBottom: 10 }} data-testid="question-limit-signup-btn">
          Create an account to continue
        </button>
        <button
          onClick={onWaitlist}
          style={{ width: '100%', background: 'none', border: '1.5px solid var(--border-strong)', borderRadius: 100, padding: '12px 24px', fontSize: 13, color: 'var(--body)', cursor: 'pointer', marginBottom: 10, fontFamily: 'inherit' }}
          data-testid="question-limit-waitlist-btn"
        >
          Join the waitlist instead
        </button>
        <button
          onClick={onFeedback}
          style={{ width: '100%', background: 'none', border: '1.5px solid var(--border-strong)', borderRadius: 100, padding: '12px 24px', fontSize: 13, color: 'var(--body)', cursor: 'pointer', marginBottom: 10, fontFamily: 'inherit' }}
          data-testid="question-limit-feedback-btn"
        >
          Share your thoughts to shape the next phase
        </button>
        <button
          onClick={onDismiss}
          style={{ background: 'none', border: 'none', fontSize: 12, color: 'var(--muted)', cursor: 'pointer', textDecoration: 'underline', padding: '4px 0' }}
          data-testid="question-limit-dismiss-btn"
        >
          Not right now
        </button>
      </div>
    </div>
  );
}

// Waitlist modal (standalone email capture)
function WaitlistModal({ onClose, onSuccess, defaultName }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState(defaultName || '');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/email-capture`, { email, name, source: 'waitlist_after_reading' });
    } catch { /* silent */ }
    setDone(true);
    setTimeout(() => { onSuccess?.(); }, 1500);
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 260 }} data-testid="waitlist-modal">
      <div className="modal-box">
        <div className="modal-handle" />
        {!done ? (
          <>
            <div className="modal-title" style={{ marginBottom: 6 }}>Join the waitlist</div>
            <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.7 }}>
              Family profiles, saved readings, and deeper blueprints are coming. Be first to know.
            </p>
            <form onSubmit={handleSubmit}>
              <label className="field-label">Your name</label>
              <input className="field-input" value={name} onChange={e => setName(e.target.value)} placeholder="First name" style={{ marginBottom: 10 }} />
              <label className="field-label">Email address</label>
              <input className="field-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" required />
              <button type="submit" className="modal-close" style={{ marginTop: 20 }} data-testid="waitlist-submit-btn">
                Join the waitlist
              </button>
            </form>
            <button onClick={onClose} style={{ background: 'none', border: 'none', width: '100%', textAlign: 'center', marginTop: 12, fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }}>
              No thanks
            </button>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 28, marginBottom: 12 }}>✦</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, marginBottom: 8 }}>You're on the list</div>
            <p style={{ fontSize: 14, color: 'var(--muted)' }}>We'll be in touch when family profiles launch.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ToolPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const bodyRef = useRef(null);
  const printRef = useRef(null);
  const [searchParams] = useSearchParams();

  // Form state
  const [mode, setMode] = useState('child');
  const [name, setName] = useState('');
  const [marriedName, setMarriedName] = useState('');
  const [showMarried, setShowMarried] = useState(false);
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [stage, setStage] = useState('');
  const [pronouns, setPronouns] = useState(''); // '', 'he/him', 'she/her', 'they/them'
  const [relationship, setRelationship] = useState(''); // freeform when mode === 'other'

  // Pre-fill from URL params (coming from Family Dashboard)
  useEffect(() => {
    const n = searchParams.get('name');
    const d = searchParams.get('day');
    const m = searchParams.get('month');
    const y = searchParams.get('year');
    const md = searchParams.get('mode');
    const p = searchParams.get('pronouns');
    if (n) setName(n);
    if (d) setDay(d);
    if (m) setMonth(m);
    if (y) setYear(y);
    if (md) setMode(md);
    if (p) setPronouns(p);
  }, [searchParams]);

  // Page state
  const [step, setStep] = useState('form');
  const [reading, setReading] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [loadingMsg, setLoadingMsg] = useState(LOADING_MESSAGES[0]);

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatting, setIsChatting] = useState(false);
  const [qaUsed, setQaUsed] = useState([]);

  // Save state
  const [readingSaved, setReadingSaved] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);

  // Share + Email-me state
  const [shareState, setShareState] = useState('idle'); // idle | copied
  const [emailState, setEmailState] = useState('idle'); // idle | sending | sent | error
  const [pendingShare, setPendingShare] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(false);

  // Reading limit
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);

  // Question limit (1 follow-up question before prompting sign-up + waitlist + feedback)
  const QUESTION_LIMIT = 1;
  const [showQuestionLimitModal, setShowQuestionLimitModal] = useState(false);

  // Preview numbers
  const [preview, setPreview] = useState(null);
  const [starSign, setStarSign] = useState(null);

  // Loading message cycle
  useEffect(() => {
    if (step !== 'loading') return;
    let i = 0;
    const t = setInterval(() => {
      i = (i + 1) % LOADING_MESSAGES.length;
      setLoadingMsg(LOADING_MESSAGES[i]);
    }, 2000);
    return () => clearInterval(t);
  }, [step]);

  // Compute preview numbers client-side
  useEffect(() => {
    if (name.trim() && day && month && year) {
      try {
        setPreview(calculateAll(name.trim(), parseInt(day), parseInt(month), parseInt(year)));
      } catch { setPreview(null); }
    } else { setPreview(null); }
    if (day && month) {
      setStarSign(getStarSign(parseInt(month), parseInt(day)));
    } else { setStarSign(null); }
  }, [name, day, month, year]);

  // Scroll to top of the reading body whenever we land on the reading step
  useEffect(() => {
    if (step === 'reading') {
      // Scroll the outer window AND the reading body container to top
      window.scrollTo({ top: 0, behavior: 'auto' });
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }
  }, [step]);

  // Scroll to bottom on new chat messages
  useEffect(() => {
    if (chatMessages.length > 0 && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const isFormValid = name.trim() && day && month && year && stage;

  const handleReveal = async () => {
    if (!isFormValid) return;
    return await runReveal();
  };

  // Internal — actually fires the API call. Split from `handleReveal` so we
  // can call it again after a successful account-lite signup/sign-in without
  // making the user click "Reveal" twice.
  const runReveal = async () => {
    // GATE — anonymous visitors only get LIMIT free readings. If they've used
    // them all, show the create-account modal BEFORE we generate (so they
    // never get cut off mid-reading).
    if (!user && getReadingCount() >= LIMIT) {
      setShowLimitModal(true);
      return;
    }
    setStep('loading');
    setLoadingMsg(LOADING_MESSAGES[0]);
    setChatMessages([]);
    setQaUsed([]);
    setReadingSaved(false);
    setErrorMsg('');
    try {
      const { data } = await axios.post(`${API}/readings/generate`, {
        name: name.trim(),
        day: parseInt(day), month: parseInt(month), year: parseInt(year),
        stage, mode,
        pronouns: pronouns || null,
        relationship: mode === 'other' ? (relationship.trim() || null) : null,
      }, {
        timeout: 240000, // 240s — covers Sonnet retries + Haiku fallback during upstream outages
      });
      // Guard: if the reading text came back empty/whitespace, treat as a failed generation
      if (!data || !data.reading || !String(data.reading).trim()) {
        setErrorMsg("We couldn't quite finish your reading — the AI returned an empty response. Please try again.");
        setStep('error');
        return;
      }
      setReading(data);
      setStep('reading');
      // Quietly count this reading. The gate will fire when they try to
      // generate the NEXT one, never mid-read.
      if (!user) {
        incrementReadingCount();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const isTimeout = err?.code === 'ECONNABORTED' || /timeout/i.test(String(err?.message || ''));
      setErrorMsg(
        isTimeout
          ? "The reading took longer than expected. Please try again — it usually comes through in about 20 seconds."
          : (detail || 'Something went wrong generating the reading. Please try again.')
      );
      setStep('error');
    }
  };

  const handleChat = async (question) => {
    if (!question.trim() || isChatting || !reading) return;
    // Enforce 3-question limit for anonymous users
    if (!user && chatMessages.filter(m => m.role === 'user').length >= QUESTION_LIMIT) {
      setShowQuestionLimitModal(true);
      return;
    }
    const q = question.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', text: q }]);
    setIsChatting(true);
    try {
      const { data } = await axios.post(`${API}/readings/chat`, { session_id: reading.session_id, question: q });
      setChatMessages(prev => [...prev, { role: 'ai', text: data.response }]);
      // After the 3rd answer is received, show the prompt
      if (!user && chatMessages.filter(m => m.role === 'user').length + 1 >= QUESTION_LIMIT) {
        setTimeout(() => setShowQuestionLimitModal(true), 1200);
      }
    } catch {
      setChatMessages(prev => [...prev, { role: 'ai', text: "I wasn't able to respond just now. Please try again." }]);
    } finally { setIsChatting(false); }
  };

  const doSaveReading = async () => {
    if (!reading) return;
    const token = getToken();
    if (!token) return;
    try {
      await axios.post(`${API}/readings/save`, { session_id: reading.session_id }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReadingSaved(true);
      setPendingSave(false);
    } catch (err) { console.error('Save failed', err); }
  };

  const handleSave = () => {
    if (user) { doSaveReading(); }
    else { setPendingSave(true); setShowAuthModal(true); }
  };

  const doShare = async () => {
    if (!reading) return;
    // Make sure the reading is saved first (owner assoc.) if user is logged in
    if (user && !readingSaved) await doSaveReading();
    const url = `${window.location.origin}/r/${reading.session_id}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${reading.first_name || reading.name}'s blueprint reading`,
          text: 'A numerology blueprint reading from Little Blue Numbers',
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        setShareState('copied');
        setTimeout(() => setShareState('idle'), 2400);
      }
    } catch { /* user cancelled share */ }
  };

  const handleShare = () => {
    if (user) { doShare(); }
    else { setPendingShare(true); setShowAuthModal(true); }
  };

  const doEmailMe = async () => {
    if (!reading) return;
    setEmailState('sending');
    try {
      const token = getToken();
      if (user && !readingSaved) await doSaveReading();
      await axios.post(`${API}/readings/email-me`, { session_id: reading.session_id }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmailState('sent');
      setTimeout(() => setEmailState('idle'), 3200);
    } catch (err) {
      console.error('email-me failed', err);
      setEmailState('error');
      setTimeout(() => setEmailState('idle'), 3200);
    }
  };

  const handleEmailMe = () => {
    if (user) { doEmailMe(); }
    else { setPendingEmail(true); setShowAuthModal(true); }
  };

  const handleAuthSuccess = () => {
    const wasOnFormStep = step === 'form' && isFormValid && !reading;
    setShowAuthModal(false);
    setShowLimitModal(false);
    resetReadingCount();
    if (pendingSave) { setPendingSave(false); setTimeout(() => doSaveReading(), 200); }
    if (pendingShare) { setPendingShare(false); setTimeout(() => doShare(), 250); }
    if (pendingEmail) { setPendingEmail(false); setTimeout(() => doEmailMe(), 300); }
    // If they signed in/up FROM the form page (i.e. they were trying to see
    // a reading and hit the limit gate), auto-start the reading now.
    if (wasOnFormStep) {
      setTimeout(() => runReveal(), 250);
    }
  };

  const handlePrint = () => {
    if (!reading) return;
    try {
      const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 56;
      const textW = pageW - margin * 2;
      let y = margin;

      const ensureSpace = (needed) => {
        if (y + needed > pageH - margin) {
          doc.addPage();
          y = margin;
        }
      };

      // Brand header
      doc.setFont('times', 'normal');
      doc.setFontSize(22);
      doc.setTextColor(45, 74, 111); // brand blue
      doc.text('little blue numbers', pageW / 2, y, { align: 'center' });
      y += 16;
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 130);
      doc.text('BLUEPRINT READING', pageW / 2, y, { align: 'center' });
      y += 30;

      // Subject name + stage
      doc.setFont('times', 'italic');
      doc.setFontSize(28);
      doc.setTextColor(26, 26, 46);
      doc.text(firstName || reading.first_name || reading.name || 'Reading', pageW / 2, y, { align: 'center' });
      y += 24;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(120, 120, 130);
      doc.text(reading.stage_label || '', pageW / 2, y, { align: 'center' });
      y += 28;

      // Numbers row
      const n = reading.numbers || {};
      const chips = [
        `Life Path ${fmtNum(n.life_path)}`,
        `Expression ${fmtNum(n.expression)}`,
        `Soul Urge ${fmtNum(n.soul_urge)}`,
        `Personality ${fmtNum(n.personality)}`,
        `Birthday ${fmtNum(n.birthday)}`,
        `Maturity ${fmtNum(n.maturity)}`,
        `${n.star_sign || ''}`,
      ].filter(Boolean).join('  ·  ');
      doc.setFontSize(10);
      doc.setTextColor(45, 74, 111);
      const chipLines = doc.splitTextToSize(chips, textW);
      doc.text(chipLines, pageW / 2, y, { align: 'center' });
      y += chipLines.length * 14 + 22;

      // Divider
      doc.setDrawColor(220, 215, 210);
      doc.setLineWidth(0.6);
      doc.line(margin, y, pageW - margin, y);
      y += 24;

      // Reading body — section by section. Strip ** markers since we render
      // bold via the PDF setFontStyle directly per word group.
      const text = (reading.reading || '').replace(/\r/g, '');
      const blocks = text.split(/\n\n+/);

      const KNOWN_HEADINGS_LOCAL = [
        // v3 (Mar 2026)
        'Your blueprint at a glance', 'Their blueprint at a glance',
        'Who you are at your core', 'Who they are at their core',
        'Where you hold tension', 'Where they hold tension',
        'How you show up in the world', 'How they show up in the world',
        'What your soul is really looking for', 'What their soul is really looking for',
        'Where you are right now', 'Where they are right now',
        "Who you're becoming", "Who they're becoming",
        "Here's what we used to build your reading",
        // legacy
        'Their Blueprint','Their Shadow','Their Strength','How to Love Them Well',
        'Your Blueprint','Your Shadow','Your Strength','How to Love Yourself Well',
        "This Year's Invitation",
      ];

      blocks.forEach((block) => {
        const trimmed = block.trim();
        if (!trimmed) return;
        const isHeading = KNOWN_HEADINGS_LOCAL.includes(trimmed)
          || trimmed.startsWith('## ')
          || isBreakdownSubheading(trimmed);
        if (isHeading) {
          ensureSpace(48);
          y += 8;
          doc.setFont('times', 'normal');
          doc.setFontSize(18);
          doc.setTextColor(31, 53, 86);
          doc.text(trimmed.replace(/^##\s*/, ''), margin, y);
          y += 22;
        } else {
          // Strip the ** markers; rendering true bold inline in jsPDF is
          // fiddly across line wraps. The PDF stays clean and warm without it.
          const clean = trimmed.replace(/\*\*([^*]+)\*\*/g, '$1');
          doc.setFont('times', 'normal');
          doc.setFontSize(12);
          doc.setTextColor(60, 50, 70);
          const lines = doc.splitTextToSize(clean, textW);
          // Manual line spacing for breathing room (1.55x)
          const lineH = 18;
          lines.forEach((line) => {
            ensureSpace(lineH);
            doc.text(line, margin, y);
            y += lineH;
          });
          y += 12;
        }
      });

      // Footer on last page
      ensureSpace(60);
      y = pageH - margin - 20;
      doc.setDrawColor(220, 215, 210);
      doc.line(margin, y, pageW - margin, y);
      y += 14;
      doc.setFont('times', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(120, 120, 130);
      doc.text('With warmth — Jemma', pageW / 2, y, { align: 'center' });
      y += 12;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text('littlebluenumbers.com', pageW / 2, y, { align: 'center' });

      const safeName = (firstName || reading.first_name || 'reading').replace(/[^a-z0-9_-]+/gi, '-').toLowerCase();
      doc.save(`little-blue-numbers-${safeName}.pdf`);
    } catch (e) {
      console.error('PDF generation failed:', e);
      alert("We couldn't generate the PDF. Please try again or use the 'Email me this reading' button.");
    }
  };

  const resetTool = () => {
    setStep('form');
    setReading(null);
    setChatMessages([]);
    setQaUsed([]);
    setReadingSaved(false);
  };

  const nums = reading?.numbers || {};
  const qaSuggestions = mode === 'child' ? CHILD_QA : SELF_QA;

  // Typewriter reveal — only animate the *first* time a particular reading shows.
  // Once the same session_id has finished typing, navigating back/forward won't
  // re-animate (it stays as the full text).
  const [typedSessionId, setTypedSessionId] = React.useState(null);
  const shouldType = !!reading && typedSessionId !== reading.session_id;
  const { displayed: typedText, isTyping } = useTypewriter(
    shouldType ? reading?.reading : '',
    shouldType
  );
  useEffect(() => {
    if (reading && !isTyping && shouldType && typedText && typedText.length >= (reading.reading || '').length) {
      setTypedSessionId(reading.session_id);
    }
  }, [isTyping, shouldType, typedText, reading]);

  const readingTextToRender = shouldType ? typedText : (reading?.reading || '');
  const parsedReading = readingTextToRender ? parseReading(readingTextToRender) : [];
  const firstName = reading?.first_name || name.split(' ')[0] || 'them';

  // Accuracy prompt — show once per reading, after the typewriter has finished
  const [showAccuracyPrompt, setShowAccuracyPrompt] = useState(true);
  useEffect(() => {
    if (reading?.session_id) {
      setShowAccuracyPrompt(!hasAccuracyVoted(reading.session_id));
    }
  }, [reading?.session_id]);

  return (
    <div className="reading-page" data-step={step}>
      {/* Printable version — hidden on screen */}
      {reading && <PrintableReading reading={reading} ref={printRef} />}

      {/* READING HEADER (gradient) */}
      {step === 'reading' && reading && (
        <div className="reading-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <button
              onClick={resetTool}
              style={{ background: 'rgba(255,255,255,0.14)', border: 'none', borderRadius: 100, padding: '6px 14px', color: 'white', fontSize: 12, cursor: 'pointer', fontFamily: "'Figtree', sans-serif" }}
              data-testid="back-to-form-btn"
            >← Back</button>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Your KinnCode</div>
          </div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 300, color: 'white' }}>
            {reading.name}
          </div>
          <div className="header-chips">
            <span className="chip">{nums.star_sign}</span>
            <span className="chip">Life Path {fmtNum(nums.life_path)}</span>
            <span className="chip">Expression {fmtNum(nums.expression)}</span>
            <span className="chip">Soul Urge {fmtNum(nums.soul_urge)}</span>
            <span className="chip">Personality {fmtNum(nums.personality)}</span>
            <span className="chip">Birthday {fmtNum(nums.birthday)}</span>
            <span className="chip">Maturity {fmtNum(nums.maturity)}</span>
            <span className="chip">{reading.stage_label}</span>
          </div>
        </div>
      )}

      {/* SCROLLABLE BODY */}
      <div className="reading-body" ref={bodyRef}>
        <div className="reading-body-inner">

          {/* ── FORM ── */}
          {step === 'form' && (
            <div className="tool-form-card" data-testid="tool-form-card">
              <div style={{ padding: '20px 0 8px' }}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 300, color: 'var(--deep)', marginBottom: 4 }}>Your KinnCode</div>
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>Enter details to reveal the blueprint</div>
              </div>

              <div className="mode-question" data-testid="mode-question">Who is this reading for?</div>
              <div className="toggle-wrap toggle-wrap-3" style={{ marginTop: 4, marginBottom: 18 }}>
                <button className={`toggle-btn ${mode === 'child' ? 'active' : ''}`} onClick={() => { setMode('child'); setStage(''); }} data-testid="toggle-child">My child</button>
                <button className={`toggle-btn ${mode === 'self' ? 'active' : ''}`} onClick={() => { setMode('self'); setStage(''); }} data-testid="toggle-self">Myself</button>
                <button className={`toggle-btn ${mode === 'other' ? 'active' : ''}`} onClick={() => { setMode('other'); setStage(''); }} data-testid="toggle-other">Someone else</button>
              </div>

              {mode === 'other' && (
                <div style={{ marginBottom: 18 }}>
                  <label className="field-label">Who is this for?</label>
                  <input
                    className="field-input"
                    value={relationship}
                    onChange={e => setRelationship(e.target.value)}
                    placeholder="e.g. my partner, my sister, my best friend"
                    maxLength={60}
                    data-testid="relationship-input"
                  />
                  <div className="field-hint">A word or two is plenty — this shapes the tone of the reading.</div>
                </div>
              )}

              <label className="field-label">{mode === 'child' ? "Child's full name" : mode === 'other' ? 'Their full name' : 'Your full name'}</label>
              <input className="field-input" value={name} onChange={e => setName(e.target.value)} placeholder="Enter full name" autoComplete="off" data-testid="name-input" />

              {mode === 'self' && (
                <div style={{ marginTop: 8 }}>
                  {!showMarried ? (
                    <button className="married-toggle" onClick={() => setShowMarried(true)} data-testid="add-married-name-btn">+ Add married / current name</button>
                  ) : (
                    <div>
                      <label className="field-label">Married / current name</label>
                      <input className="field-input" value={marriedName} onChange={e => setMarriedName(e.target.value)} placeholder="Current name (if different)" data-testid="married-name-input" />
                    </div>
                  )}
                </div>
              )}

              <label className="field-label">{mode === 'child' ? "Child's date of birth" : mode === 'other' ? 'Their date of birth' : 'Your date of birth'}</label>
              <div className="dob-wrap">
                <select value={day} onChange={e => setDay(e.target.value)} data-testid="day-select">
                  <option value="">Day</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <select value={month} onChange={e => setMonth(e.target.value)} data-testid="month-select">
                  <option value="">Month</option>
                  {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
                <select value={year} onChange={e => setYear(e.target.value)} data-testid="year-select">
                  <option value="">Year</option>
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>

              {starSign && (
                <div className="sign-detected" data-testid="sign-detected">{starSign.symbol} {starSign.name}</div>
              )}

              <div>
                <label className="field-label" style={{ marginTop: 18 }}>
                  {mode === 'child' ? 'Their pronouns' : mode === 'other' ? 'Their pronouns' : 'Your pronouns'}
                  <span style={{ color: 'var(--muted)', fontWeight: 400, marginLeft: 6, fontSize: 11, letterSpacing: 0 }}>(optional)</span>
                </label>
                <div className="pronoun-buttons" data-testid="pronoun-buttons" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                  {[
                    { v: 'he/him', l: 'He / Him' },
                    { v: 'she/her', l: 'She / Her' },
                    { v: 'they/them', l: 'They / Them' },
                  ].map(p => (
                    <button
                      key={p.v}
                      type="button"
                      className={`stage-btn ${pronouns === p.v ? 'active' : ''}`}
                      onClick={() => setPronouns(prev => prev === p.v ? '' : p.v)}
                      data-testid={`pronoun-${p.v.replace('/', '-')}`}
                      style={{ flex: '1 1 30%', minWidth: 90, padding: '10px 12px' }}
                    >
                      <span>{p.l}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="field-label" style={{ marginTop: 18 }}>{mode === 'child' ? 'Their life stage' : mode === 'other' ? 'Their life stage' : 'Your life stage'}</label>
                <div className="stage-buttons" data-testid="stage-buttons">
                  {STAGES.map(s => (
                    <button key={s.id} className={`stage-btn ${stage === s.id ? 'active' : ''}`} onClick={() => setStage(s.id)} data-testid={`stage-${s.id}`}>
                      <span>{s.label}</span>
                      <span style={{ fontSize: 11, opacity: 0.6 }}>{s.age}</span>
                    </button>
                  ))}
                </div>
              </div>

              {preview && (
                <div className="preview-box" data-testid="preview-box">
                  <div className="preview-label">{mode === 'self' ? 'Your numbers' : 'Their numbers'}</div>
                  <div className="chips-wrap">
                    <span className="preview-chip">Life Path {fmtNum(preview.life_path)}</span>
                    <span className="preview-chip">Expression {fmtNum(preview.expression)}</span>
                    <span className="preview-chip">Soul Urge {fmtNum(preview.soul_urge)}</span>
                    <span className="preview-chip">Personality {fmtNum(preview.personality)}</span>
                    <span className="preview-chip">Birthday {fmtNum(preview.birthday)}</span>
                    <span className="preview-chip">Maturity {fmtNum(preview.maturity)}</span>
                    <span className="preview-chip">{preview.star_sign}</span>
                  </div>
                </div>
              )}

              <button className="reveal-btn" disabled={!isFormValid} onClick={handleReveal} data-testid="reveal-btn">
                {mode === 'self' ? 'Reveal my blueprint' : 'Reveal their blueprint'}
              </button>
              {!user && (
                <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--muted)', marginTop: 12, letterSpacing: '0.02em' }} data-testid="free-readings-counter">
                  {(() => {
                    const remaining = Math.max(0, LIMIT - getReadingCount());
                    if (remaining === LIMIT) return 'One reading included · No sign-up needed';
                    if (remaining === 0) return "You've used your reading — create an account to keep going";
                    return remaining + ' reading remaining';
                  })()}
                </div>
              )}
              <div style={{ height: 40 }} />
            </div>
          )}

          {/* ── LOADING ── */}
          {step === 'loading' && (
            <div className="loading-wrap" data-testid="loading-state">
              <div className="loading-dots"><div className="dot" /><div className="dot" /><div className="dot" /></div>
              <div className="loading-text">{loadingMsg}</div>
            </div>
          )}

          {/* ── ERROR ── */}
          {step === 'error' && (
            <div className="error-box" data-testid="error-state">
              <div className="error-icon">✦</div>
              <div className="error-msg">{errorMsg}</div>
              <button className="retry-btn" onClick={handleReveal} data-testid="retry-btn">Try again</button>
              <button className="back-btn-sm" onClick={resetTool} data-testid="back-from-error-btn">Go back</button>
            </div>
          )}

          {/* ── READING ── */}
          {step === 'reading' && reading && (
            <div data-testid="reading-content">
              {/* SAVE + PDF + SHARE + EMAIL ROW */}
              <div className="reading-actions-row">
                {!readingSaved ? (
                  <button className="save-btn" onClick={handleSave} data-testid="save-reading-btn">
                    {user ? 'Save this reading' : 'Save it'}
                  </button>
                ) : (
                  <span className="saved-badge" data-testid="saved-badge">✓ Saved</span>
                )}
                <button className="pdf-btn" onClick={handlePrint} data-testid="pdf-btn">Download PDF</button>
                <button className="share-btn" onClick={handleShare} data-testid="share-btn">
                  {shareState === 'copied' ? '✓ Link copied' : 'Share'}
                </button>
                <button
                  className="emailme-btn"
                  onClick={handleEmailMe}
                  disabled={emailState === 'sending'}
                  data-testid="emailme-btn"
                >
                  {emailState === 'sending' && 'Sending...'}
                  {emailState === 'sent' && '✓ Sent to your inbox'}
                  {emailState === 'error' && 'Try again'}
                  {emailState === 'idle' && 'Email me this'}
                </button>
              </div>

              {/* READING CARD — ADHD/dyslexia friendly with headings + bold keywords */}
              <div className={`reading-card ${isTyping ? 'is-typing' : ''}`} data-testid="reading-card">
                {parsedReading.map((seg, i) =>
                  seg.type === 'heading'
                    ? <h3 key={i} className="reading-heading">{seg.text}</h3>
                    : <p key={i}>{renderRichText(seg.text)}</p>
                )}
                {isTyping && <span className="reading-caret" aria-hidden="true" />}
              </div>

              {/* ACCURACY PROMPT — appears once typewriter finishes, vanishes after vote */}
              {showAccuracyPrompt && !isTyping && reading?.session_id && (
                <ReadingAccuracyPrompt
                  sessionId={reading.session_id}
                  onDone={() => setShowAccuracyPrompt(false)}
                />
              )}

              {/* CTA — closing line + dual action (only shown for anonymous users) */}
              {!user && (
                <div className="resonated-box" data-testid="resonated-box">
                  <div className="resonated-icon">✦</div>
                  <div className="resonated-text">
                    <strong>This is just the beginning of their blueprint.</strong>
                    <span>Create an account and join our waitlist — be first to know when full readings, family profiles and saved blueprints launch.</span>
                  </div>
                  <div className="resonated-actions">
                    <button
                      className="waitlist-btn"
                      onClick={() => setShowLimitModal(true)}
                      data-testid="bottom-create-account-btn"
                    >
                      Create an account &amp; join our waitlist
                    </button>
                    <button
                      type="button"
                      className="resonated-signin-link"
                      onClick={() => setShowAuthModal(true)}
                      data-testid="bottom-signin-link"
                    >
                      I already have an account
                    </button>
                  </div>
                </div>
              )}

              {/* Q&A SUGGESTIONS */}
              {chatMessages.length === 0 && (
                <div className="qa-box" data-testid="qa-box">
                  <div className="qa-label">Ask anything</div>
                  <div className="qa-btns">
                    {qaSuggestions.filter(q => !qaUsed.includes(q)).map(q => (
                      <button key={q} className="qa-btn" onClick={() => { setQaUsed(p => [...p, q]); handleChat(q); }} data-testid="qa-btn">{q}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* CHAT MESSAGES */}
              {chatMessages.length > 0 && (
                <div className="chat-messages" data-testid="chat-messages">
                  {chatMessages.map((msg, i) => (
                    <div key={i} className="msg-wrap">
                      {msg.role === 'user'
                        ? <span className="msg-user">{msg.text}</span>
                        : <div className="msg-ai">{renderText(msg.text)}</div>}
                    </div>
                  ))}
                  {isChatting && (
                    <div className="msg-wrap">
                      <div className="msg-ai">
                        <div className="loading-dots" style={{ justifyContent: 'flex-start', margin: '4px 0' }}>
                          <div className="dot" /><div className="dot" /><div className="dot" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div style={{ height: 20 }} />
            </div>
          )}
        </div>
      </div>

      {/* STICKY CHAT INPUT */}
      {step === 'reading' && (
        <div className="chat-input-wrap" data-testid="chat-input-wrap">
          <div className="chat-row">
            <textarea
              rows={1} placeholder="Ask a question about this reading..."
              value={chatInput} onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChat(chatInput); } }}
              data-testid="chat-input"
            />
            <button className="chat-send-btn" onClick={() => handleChat(chatInput)} disabled={isChatting || !chatInput.trim()} data-testid="chat-send-btn">Send</button>
          </div>
          <button className="reset-btn" onClick={resetTool} data-testid="new-reading-btn">Start a new reading</button>
        </div>
      )}

      {/* AUTH MODAL */}
      {showAuthModal && (
        <AuthModal onClose={() => { setShowAuthModal(false); setPendingSave(false); }} onSuccess={handleAuthSuccess} initialTab="register" />
      )}

      {/* READING LIMIT MODAL — account-lite gate after first free reading */}
      {showLimitModal && !showAuthModal && (
        <ReadingLimitModal
          defaultName={firstName}
          onSuccess={() => {
            setShowLimitModal(false);
            resetReadingCount();
            // The user gave us their email to keep going — immediately
            // generate the reading they were trying to see, instead of
            // making them click "Reveal my blueprint" a second time.
            setTimeout(() => runReveal(), 80);
          }}
          onSignIn={() => { setShowLimitModal(false); setShowAuthModal(true); }}
          onDismiss={() => { setShowLimitModal(false); resetReadingCount(); }}
        />
      )}

      {/* QUESTION LIMIT MODAL (3 follow-up questions) */}
      {showQuestionLimitModal && !showAuthModal && (
        <QuestionLimitModal
          onSignUp={() => { setShowQuestionLimitModal(false); setShowAuthModal(true); }}
          onWaitlist={() => { setShowQuestionLimitModal(false); setShowWaitlistModal(true); }}
          onFeedback={() => {
            setShowQuestionLimitModal(false);
            window.dispatchEvent(new CustomEvent('lbn:open-feedback'));
          }}
          onDismiss={() => setShowQuestionLimitModal(false)}
        />
      )}

      {/* WAITLIST MODAL */}
      {showWaitlistModal && (
        <WaitlistModal
          onClose={() => setShowWaitlistModal(false)}
          onSuccess={() => setShowWaitlistModal(false)}
          defaultName={firstName}
        />
      )}
    </div>
  );
}
